import { useEffect } from "react";
import { useDeferUntilInteraction } from "@/lib/defer";

// Systemic Digital / LeadConnector inline form — embedded exactly as provided.
// The form_embed.js script auto-resizes the iframe to its natural height.
const FORM_ID = "2NeiKF5FmnmFC2MdbMuF";
const FORM_SRC = `https://link.systemic-digital.net/widget/form/${FORM_ID}`;
const SCRIPT_SRC = "https://link.systemic-digital.net/js/form_embed.js";
const NATURAL_HEIGHT = 744; // provider-declared natural height (data-height)

interface Props {
  /** Unique suffix so the hero and popup instances get distinct iframe ids. */
  instanceId: string;
  className?: string;
  /**
   * When true, hold the third-party iframe (which sets cookies and pulls a
   * large bundle) until the visitor's first interaction. Used for the always-on
   * hero form so it stays off the initial critical path; the popup form loads
   * immediately because opening it IS the interaction.
   */
  defer?: boolean;
}

const LeadForm = ({ instanceId, className, defer = false }: Props) => {
  const interacted = useDeferUntilInteraction();
  const ready = !defer || interacted;

  useEffect(() => {
    if (!ready) return;
    if (!document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
      const s = document.createElement("script");
      s.src = SCRIPT_SRC;
      s.async = true;
      document.body.appendChild(s);
    } else {
      // Nudge the already-loaded script to (re)attach to newly mounted iframes.
      window.dispatchEvent(new Event("resize"));
    }
  }, [ready]);

  const uid = `inline-${FORM_ID}-${instanceId}`;

  // Reserve the exact space so revealing the form causes no layout shift.
  if (!ready) {
    return (
      <div
        className={className}
        style={{ width: "100%", minHeight: NATURAL_HEIGHT, borderRadius: "10px", display: "grid", placeItems: "center" }}
        aria-label="Free estimate form loading"
        role="status"
      >
        <span className="text-muted-foreground text-sm">Loading form…</span>
      </div>
    );
  }

  return (
    <iframe
      src={FORM_SRC}
      id={uid}
      title="Get a Free Fencing Estimate"
      className={className}
      style={{
        width: "100%",
        minHeight: NATURAL_HEIGHT,
        border: "none",
        borderRadius: "10px",
        display: "block",
      }}
      data-layout="{'id':'INLINE'}"
      data-trigger-type="alwaysShow"
      data-trigger-value=""
      data-activation-type="alwaysActivated"
      data-activation-value=""
      data-deactivation-type="neverDeactivate"
      data-deactivation-value=""
      data-form-name="Website Form"
      data-height={NATURAL_HEIGHT}
      data-layout-iframe-id={uid}
      data-form-id={FORM_ID}
      scrolling="no"
    />
  );
};

export default LeadForm;
